/* eslint-disable no-restricted-imports */
import * as React from 'react';
import { BentoTypographyVariant } from 'theme/typography';
import {
  Typography as MuiTypography,
  TypographyProps as MuiTypographyProps,
} from '@material-ui/core';
import {
  typographyBase,
  typographyColor,
  typographyVariant,
} from 'bento/Typography/typographyCssDefs';

export interface TypographyProps
  extends Pick<MuiTypographyProps, Exclude<keyof MuiTypographyProps, 'color' | 'variant'>> {
  /**
   * As of 4/2023 the lighter text color should be limited to disabled text due to the low
   * contrast. @todo evaluate removing/renaming this in the typography work
   */
  color?:
    | 'default'
    | 'subtle'
    | 'disabled'
    | 'contrast'
    | 'primaryBase'
    | 'primaryDark'
    | 'successBase'
    | 'successDark'
    | 'warningBase'
    | 'warningDark'
    | 'dangerBase'
    | 'dangerDark'
    | 'internalBase'
    | 'internalDark'
    | 'inherit';
  variant?: Variant;
  component?: React.ElementType;
  mb?: number;
  uppercase?: boolean;
}

export type Variant = BentoTypographyVariant | 'srOnly' | 'inheritFont';

const typographyElementMapping: Record<Variant, React.ElementType> = {
  decorative: 'h1',
  heading01: 'h1',
  heading02: 'h2',
  heading03: 'h3',
  heading04: 'h4',
  overline: 'p',
  body: 'p',
  bodyStrong: 'p',
  caption: 'p',
  captionStrong: 'p',
  srOnly: 'p',
  inheritFont: 'span',
};

/**
 * Use `Typography` for text
 *
 * ```js
 * import { Typography, TypographyProps } from 'bento';
 * ```
 * @deprecated Deprecated — use pantry Text instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-text--docs
 * Migration: Replace Typography with the pantry Text component.
 */
export const Typography = React.forwardRef<React.ElementType, TypographyProps>(
  (
    {
      color = 'default',
      variant = 'body',
      uppercase = false,
      className,
      component,
      paragraph = false,
      mb = 0,
      ...rest
    },
    ref
  ) => {
    const bottomMargin = paragraph ? 2 : mb;
    const defaultComponent = paragraph ? 'p' : typographyElementMapping[variant];

    return (
      <MuiTypography
        className={className}
        component={component || defaultComponent}
        css={[
          typographyBase(bottomMargin, uppercase),
          typographyColor(color),
          typographyVariant(variant),
        ]}
        variant="inherit" // Assures none of the MUI typography variants are applied. Without this 'body1` class is applied to each typography instance
        ref={ref}
        {...rest}
      />
    );
  }
);
